/**
 * Фраза-чанк — единица кармана и историй.
 *
 * Карман хранит не слова, а конструкции («I would like a coffee»): смысл и
 * грамматику несёт чанк целиком. Нормализованный ключ `phraseKey` нужен
 * для дедупликации — «I'd like a coffee.» и «i’d like a coffee» одна
 * карточка. Та же нормализация повторена в SQL-миграции
 * `20261024120000_curriculum_phrases`: менять её нужно в обоих местах.
 */

import { z } from 'zod';

/** Фраза для показа ученику: не короче двух символов и не длиннее строки. */
const PhraseTextSchema = z.string().trim().min(2).max(120);

/** Нормализованный ключ фразы: регистр, апострофы, пунктуация, пробелы. */
export function phraseKey(phrase: string): string {
  return phrase
    .toLowerCase()
    .replace(/[’‘`]/g, "'")
    .replace(/[^\p{L}\p{N}' ]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Чанк истории. `id` — короткий слаг (`c1`, `would-like`), на него
 * ссылаются плейсхолдеры `{id}` в тексте и дриллы. `target` — фраза на
 * изучаемом языке урока, `translation` — её перевод на язык перевода урока
 * (`CurriculumLesson.targetLanguage` / `translationLanguage`). `isNew: false`
 * значит, что чанк пришёл из предыдущего урока и повторяется намеренно.
 * `unseen: true` ставит сервер под ученика: повтор, которого нет в его
 * кармане (например, он пришёл на ступень по тесту), — его показывают в
 * превью наравне с новыми фразами, чтобы задания не спрашивали незнакомое.
 */
export const PhraseChunkSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]{1,32}$/),
  target: PhraseTextSchema,
  translation: PhraseTextSchema,
  isNew: z.boolean(),
  unseen: z.boolean().optional(),
  /**
   * `skipped: true` — новая фраза истории, которая для этого ученика не цель
   * (короткий первый урок, сниженная нагрузка): в превью её нет, заданий на
   * неё нет, в карман она не идёт. В истории она остаётся.
   */
  skipped: z.boolean().optional(),
});
export type PhraseChunk = z.infer<typeof PhraseChunkSchema>;

/** Тело запроса «положить фразу в карман». */
export const AddPocketPhraseSchema = z.object({
  userId: z.string().uuid(),
  phrase: PhraseTextSchema,
  translation: PhraseTextSchema,
  /** Фрагмент истории, где фраза встретилась. */
  context: z.string().trim().min(1).max(400).nullish(),
  sourceLessonId: z.string().min(1).max(64).nullish(),
});
export type AddPocketPhrase = z.infer<typeof AddPocketPhraseSchema>;

/** Карточка кармана в том виде, в каком она уходит клиенту. */
export interface PocketPhrase {
  readonly id: string;
  readonly phrase: string;
  readonly translation: string;
  readonly context: string | null;
  readonly sourceLessonId: string | null;
  /** Момент следующего повторения (ISO 8601); `null` — ещё не планировалось. */
  readonly nextReviewAt: string | null;
}

/** Размер страницы кармана по умолчанию и максимальный (`GET /api/vocabulary/pocket`). */
export const POCKET_PAGE_SIZE = 30;
export const POCKET_PAGE_MAX = 100;

/**
 * Перевод английского фрагмента истории по касанию: только английский
 * (латиница) и не длиннее предложения-двух — это не общий переводчик.
 */
export const TranslateTextSchema = z.object({
  text: z
    .string()
    .trim()
    .min(1)
    .max(300)
    .refine((text) => /[A-Za-z]/.test(text) && !/[Ѐ-ӿ]/.test(text), 'Ожидается английский текст'),
});
export type TranslateText = z.infer<typeof TranslateTextSchema>;
